import { Injectable, inject } from '@angular/core';
import { NavController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, catchError, firstValueFrom, map, of, take } from 'rxjs';
import { PermissionsEnum, RolesEnum } from 'src/app/core/constants/roles-and-permissions.enum';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { normalizeInternalAppRedirect } from 'src/app/core/utils/internal-app-redirect';
import { DocumentsToLearnService } from './documents-to-learn.service';

const STORAGE_LAST_PERIODIC_CHECK_AT = 'documentsToLearnPeriodicCheckAt';
const STORAGE_REMINDER_DISMISS_COUNT = 'documentsToLearnReminderDismissCount';
const PERIODIC_CHECK_INTERVAL_MS = 1 * 60 * 60 * 1000; // интервал проверки на наличие несогласованных документов раз в час
const MAX_REMINDER_DISMISS_COUNT = 2;

/** Текст периодического напоминания (диалог показывает MenuPage). */
export const DOCUMENTS_TO_LEARN_PERIODIC_REMINDER_MESSAGE =
  'У вас есть обязательные документы, требующие подтверждения. '
  + 'Необходимо перейти в раздел Документы, ознакомиться с информацией и дать согласие.';

export type PostAuthNavigationTarget = {
  commands: string[];
  state?: Record<string, unknown>;
};

@Injectable({
  providedIn: 'root',
})
export class DocumentsToLearnComplianceService {
  private readonly documentsService = inject(DocumentsToLearnService);
  private readonly storage = inject(StorageService);
  private readonly user = inject(UserService);
  private readonly router = inject(Router);
  private readonly navCtrl = inject(NavController);

  /** true — доступ в /app заблокирован до страницы ознакомления. */
  private readonly introductionBlocked$ = new BehaviorSubject<boolean | null>(null);

  /** Сигнал MenuPage: показать диалог периодического напоминания. */
  readonly periodicReminderDialog$ = new Subject<void>();

  hasUnconfirmedDocuments(): Observable<boolean> {
    if (this.user.isExemptFromDocumentsToLearnCompliance()) {
      return of(false);
    }

    return this.documentsService.getUnconfirmedDocuments().pipe(
      map((items) => items.length > 0),
      catchError(() => of(false)),
    );
  }


  /**
   * Определяет место назначения после авторизации: introduction или /app (с state для menu).
   * @param options - опции для определения места назначения.
   * @returns Observable<PostAuthNavigationTarget> - место назначения после авторизации.
   */
  resolveDestinationAfterAuth(options: {
    isNewUser: boolean;
    needUpdate: boolean;
  }): Observable<PostAuthNavigationTarget> {
    return this.hasUnconfirmedDocuments().pipe(
      map((hasUnconfirmed) => {
        if (hasUnconfirmed) {
          this.setIntroductionBlocked(true);
          return { commands: ['/introduction'] };
        }

        this.setIntroductionBlocked(false);
        return {
          commands: ['/app'],
          state: {
            isNewUser: options.isNewUser,
            needUpdate: options.needUpdate,
            noCheck: true,
          },
        };
      }),
      take(1),
    );
  }

  /**
   * Проверяет, нужно ли проходить ознакомление с документами при восстановлении сессии.
   * @returns Observable<boolean> - true, если нужно проходить ознакомление, false — если не нужно.
   */
  checkMandatoryIntroductionOnSessionRestore(): Observable<boolean> {
    return this.hasUnconfirmedDocuments().pipe(
      map((hasUnconfirmed) => {
        this.setIntroductionBlocked(hasUnconfirmed);
        if (hasUnconfirmed) {
          this.navigateToIntroduction();
        }
        return hasUnconfirmed;
      }),
      take(1),
    );
  }

  /**
   * Проверяет, нужно ли проходить ознакомление с документами при смене роута (не чаще раза в час).
   * @param url - URL текущего роута.
   * @returns Promise<void> - void, если не нужно проходить ознакомление, Promise<void> — если нужно.
   */
  async handleRouteChange(url: string): Promise<void> {
    if (this.user.isExemptFromDocumentsToLearnCompliance()) {
      return;
    }

    if (this.isIntroductionUrl(url) || this.isDocumentsSectionUrl(url)) {
      return;
    }

    if (!(await this.shouldRunPeriodicCheck())) {
      return;
    }

    await this.recordPeriodicCheck();

    const hasUnconfirmed = await firstValueFrom(this.hasUnconfirmedDocuments());
    if (!hasUnconfirmed) {
      await this.resetReminderDismissCount();
      return;
    }

    const dismissCount = await this.getReminderDismissCount();
    if (dismissCount >= MAX_REMINDER_DISMISS_COUNT) {
      this.setIntroductionBlocked(true);
      this.navigateToIntroduction();
      return;
    }

    this.periodicReminderDialog$.next();
  }

  /** Кнопка «Перейти» в диалоге периодического напоминания. */
  onPeriodicReminderAccepted(): void {
    void this.resetReminderDismissCount();
    this.navigateToDocumentsSection();
  }

  /** Кнопка «Напомнить позже» в диалоге периодического напоминания. */
  onPeriodicReminderDismissed(): void {
    void this.incrementReminderDismissCount();
  }

  ensureAppAccessAllowed(): Observable<boolean> {
    if (this.user.isExemptFromDocumentsToLearnCompliance()) {
      return of(true);
    }

    const blocked = this.introductionBlocked$.value;
    if (blocked === true) {
      return of(false);
    }
    if (blocked === false) {
      return of(true);
    }

    return this.hasUnconfirmedDocuments().pipe(
      map((hasUnconfirmed) => {
        if (hasUnconfirmed) {
          this.setIntroductionBlocked(true);
        }
        return !hasUnconfirmed;
      }),
      take(1),
    );
  }

  setIntroductionBlocked(blocked: boolean): void {
    this.introductionBlocked$.next(blocked);
  }

  clearIntroductionBlocked(): void {
    this.setIntroductionBlocked(false);
  }

  navigateToIntroduction(): void {
    this.navCtrl.setDirection('root');
    void this.router.navigate(['/introduction'], { replaceUrl: true });
  }

  /** После страницы ознакомления — в /app без повторного checkLogin (как после логина). */
  navigateToAppAfterIntroduction(): void {
    this.clearIntroductionBlocked();
    this.navCtrl.setDirection('root');
    const redirect = normalizeInternalAppRedirect(
      this.router.parseUrl(this.router.url).queryParamMap.get('redirect'),
    );
    if (redirect) {
      void this.router.navigateByUrl(redirect, {
        replaceUrl: true,
        state: { noCheck: true },
      });
      return;
    }

    void this.router.navigate(['/app'], {
      replaceUrl: true,
      state: { noCheck: true },
    });
  }

  navigateToDocumentsSection(): void {
    if (this.canOpenManagerDocumentsTab()) {
      void this.router.navigate(['/app', 'users', 'user-documents']);
      return;
    }

    if (Boolean(this.user.data.subActive)) {
      void this.router.navigate(['/app', 'documents-to-learn']);
      return;
    }

    void this.router.navigate(['/app', 'dashboard']);
  }

  /**
   * Проверяет, является ли URL страницей документации (раздел «Документы»).
   */
  isDocumentsSectionUrl(url: string): boolean {
    const path = url.split('?')[0] ?? '';
    return path.includes('/documents-to-learn') || path.includes('/users/user-documents');
  }

  /**
   * Проверяет, является ли URL страницей ознакомления.
   */
  isIntroductionUrl(url: string): boolean {
    return (url.split('?')[0] ?? '').includes('/introduction');
  }

  private canOpenManagerDocumentsTab(): boolean {
    if (this.user.data.role === RolesEnum.ROLE_OWNER) {
      return true;
    }

    return this.user.checkPermissions([
      PermissionsEnum.PERM_USERS_READ_ATTACHED,
      PermissionsEnum.PERM_USERS_READ_ALL,
      PermissionsEnum.PERM_USERS_MANAGE_ATTACHED,
      PermissionsEnum.PERM_USERS_MANAGE_ALL,
    ]);
  }

  private async shouldRunPeriodicCheck(): Promise<boolean> {
    const lastCheckRaw = await this.storage.get(STORAGE_LAST_PERIODIC_CHECK_AT);
    const lastCheck = Number(lastCheckRaw);
    if (!lastCheckRaw || Number.isNaN(lastCheck)) {
      return true;
    }

    return Date.now() - lastCheck >= PERIODIC_CHECK_INTERVAL_MS;
  }

  private async recordPeriodicCheck(): Promise<void> {
    this.storage.set(STORAGE_LAST_PERIODIC_CHECK_AT, Date.now());
  }

  private async getReminderDismissCount(): Promise<number> {
    const raw = await this.storage.get(STORAGE_REMINDER_DISMISS_COUNT);
    const count = Number(raw);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  private async incrementReminderDismissCount(): Promise<void> {
    const count = await this.getReminderDismissCount();
    this.storage.set(STORAGE_REMINDER_DISMISS_COUNT, count + 1);
  }

  private async resetReminderDismissCount(): Promise<void> {
    this.storage.remove(STORAGE_REMINDER_DISMISS_COUNT);
  }
}
